import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { User, useAllUsersByWorkshop } from '@/api/hooks/useUsersByWorkshop'
import PageLayout from '@/components/layout/page-layout'
import { ResponsiveTabs } from '@/components/molecules/responsive-tabs'
import UsersTable from '@/components/organisms/users-table'
import { CreateInvitationDialog } from '@/components/organisms/users-table/create-invitation-dialog'
import { TabsContent } from '@/components/ui/tabs'
import { useMyWorkshops } from '@/hooks/useMyWorkshops'
import { PermissionsUtils } from '@/utils/permissions.utils'

export const Route = createFileRoute('/_authenticated/users/')({
  component: UsersPage,
})

function UsersPage() {
  const { t } = useTranslation()
  const { selectedWorkshopId } = useMyWorkshops()
  const { data: allUsers = [], isLoading } = useAllUsersByWorkshop(selectedWorkshopId)
  const navigate = useNavigate()

  const openUser = (user: User) => {
    navigate({ to: '/users/$userId', params: { userId: user.id } })
  }

  const activeUsers = allUsers.filter(user => !user.deletedAt)
  const inactiveUsers = allUsers.filter(user => user.deletedAt)

  return (
    <PageLayout title={t('global.user_plural')}>
      <ResponsiveTabs
        defaultValue="active"
        items={[
          { label: t('usersTable.activeTab'), value: 'active' },
          { label: t('usersTable.inactiveTab'), value: 'inactive' },
        ]}
        right={
          PermissionsUtils.hasPermission('user-permissions:create') && (
            <CreateInvitationDialog workshopId={selectedWorkshopId} />
          )
        }
      >
        <TabsContent value="active">
          <UsersTable
            users={activeUsers}
            isLoading={isLoading}
            workshopId={selectedWorkshopId}
            showWorkshopsColumn={!selectedWorkshopId}
            onEditUser={openUser}
          />
        </TabsContent>
        <TabsContent value="inactive">
          <UsersTable
            users={inactiveUsers}
            isLoading={isLoading}
            workshopId={selectedWorkshopId}
            variant="inactive"
            onEditUser={openUser}
          />
        </TabsContent>
      </ResponsiveTabs>
    </PageLayout>
  )
}
